// src/pages/EntitlementPage.tsx
//
// Officer id + a service date the user must explicitly choose. The date field
// starts empty and stays empty until the user picks one — there is no "today"
// fallback anywhere in this page, because a defaulted service date is "the
// single most expensive bug available in this system" (the gateway route's
// own docstring): it would answer a historical question with today's
// entitlement, produce plausible money, and be invisible.
//
// The date control is `<ServiceDatePicker>` rather than `<sgds-datepicker>`, and
// the reasoning that ruled out the SGDS control still holds: its value is
// `dd/mm/yyyy`, so consuming it means parsing a localised format on the one field
// whose entire job is "never guess the date". Our control's value is the ISO
// string this page sends to the API, with no translation layer in between. The
// compliance cost of owning a component is recorded in that file's header and in
// docs/design/sgds-reconciliation.md.
import { useId, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { ServiceDatePicker } from '../components/ServiceDatePicker'
import { api, ApiError } from '../lib/api'
import { targetValue } from '../lib/sgds-dom'
import { ErrorCard, LoadingCard } from '../components/StateCards'
import { formatIsoDate } from '../lib/format'
import type { EntitlementPeriod } from '../lib/types'

type Result =
  | { status: 'idle' }
  | { status: 'loading' }
  // I7: the resolved date lives in success state, captured from the SERVER's
  // response — never read from the `serviceDate` input at render time. That
  // input can change (or clear) after this result was fetched, and this page
  // must keep naming the date the displayed entitlement actually came from.
  | { status: 'success'; entitlement: EntitlementPeriod; resolvedServiceDate: string }
  | { status: 'error'; message: string }

export function EntitlementPage() {
  const dateId = useId()
  const dateHintId = useId()
  const [officerId, setOfficerId] = useState('')
  const [serviceDate, setServiceDate] = useState('')
  const [result, setResult] = useState<Result>({ status: 'idle' })

  const canSubmit = officerId.trim().length > 0 && serviceDate.length > 0

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    setResult({ status: 'loading' })
    try {
      const response = await api.entitlement(officerId.trim(), serviceDate)
      setResult({ status: 'success', entitlement: response.entitlement, resolvedServiceDate: response.resolvedServiceDate })
    } catch (error) {
      setResult({
        status: 'error',
        message: error instanceof ApiError ? error.message : 'Could not resolve entitlement.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Entitlement enquiry</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Entitlement is always resolved as at the <strong>date of medical service</strong>, never the bill
          submission date. This page never guesses that date for you.
        </p>
      </div>

      <form className="pt-card flex flex-col gap-5" onSubmit={handleSubmit}>
        <SgdsInput placeholder=""
          label="Officer id"
          hintText="e.g. OFF-04"
          value={officerId}
          required
          onSgdsInput={(e) => setOfficerId(targetValue(e))}
        />

        <div>
          <span id={dateId} className="block text-sm font-semibold mb-1">
            Service date <span aria-hidden="true">*</span>
            <span className="sr-only">required</span>
          </span>
          <p id={dateHintId} className="text-sm text-[var(--sgds-color-subtle)] mb-2">
            The basis for this lookup is fixed: it is always the <strong>date the medical service was
            provided</strong>. There is no submission-date option here, and no default: you must pick a date.
          </p>
          <ServiceDatePicker
            value={serviceDate}
            onChange={setServiceDate}
            labelledBy={dateId}
            describedBy={dateHintId}
          />
        </div>

        <div>
          <SgdsButton type="submit" variant="primary" disabled={!canSubmit} loading={result.status === 'loading'}>
            Resolve entitlement
          </SgdsButton>
        </div>
      </form>

      {result.status === 'loading' && <LoadingCard label="Resolving entitlement" rows={3} />}

      {result.status === 'error' && <ErrorCard title="Could not resolve entitlement" message={result.message} />}

      {result.status === 'success' && (
        <div className="pt-card">
          <h2 className="text-lg mb-4">Entitlement as at {formatIsoDate(result.resolvedServiceDate)}</h2>
          <dl className="flex flex-col gap-3 text-sm max-w-md">
            <Row label="Officer" value={result.entitlement.officerId} mono />
            <Row label="Scheme" value={result.entitlement.scheme} strong />
            <Row label="Ward class" value={result.entitlement.wardClass} />
            <Row label="Valid from" value={formatIsoDate(result.entitlement.window.from)} />
            <Row
              label="Valid until"
              value={result.entitlement.window.until !== null ? formatIsoDate(result.entitlement.window.until) : 'Open-ended'}
            />
          </dl>
        </div>
      )}
    </div>
  )
}

function Row({ label, value, mono = false, strong = false }: { label: string; value: string; mono?: boolean; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[var(--sgds-color-subtle)]">{label}</dt>
      <dd className={[mono ? 'pt-mono' : '', strong ? 'font-semibold' : ''].filter(Boolean).join(' ')}>{value}</dd>
    </div>
  )
}
